﻿import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Text, Button, Input, Tooltip } from '../../components/ui'
import { XMarkIcon, EnvelopeIcon, DocumentTextIcon, PaperClipIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import {
  QualityCategory,
  QualityImprocedentReason,
  QualityParameter,
  QualityTicket,
  QualityTicketAttachment,
  QualityTicketOrigin,
  QualityTicketStage,
  QualityTicketType,
  Severity,
  TicketKind,
  MaxDeadlineUnit,
  formatTicketNumber,
} from '../../types/quality'
import { qualityService } from '../../services/quality'
import { buildDepartmentLabelMap, DEPARTMENT_RESP_LABEL, formatOptionalDigitsField, formatProfilePickerLabel, parseOptionalDigitsField, type QualityActiveProfile } from './qualidadeDisplay'
import { TICKET_KIND_LABELS, descriptionHintForKind, descriptionLabelForKind, descriptionPlaceholderForKind, justificationHintForKind, justificationLabelForKind, ticketKindsForForm, isQualityDepartmentUser } from './qualidadeKind'
import { MAX_DEADLINE_UNIT_LABELS } from './qualityBusinessHours'
import { BrokerCombobox } from './BrokerCombobox'
import { PolicyNumberCombobox } from './PolicyNumberCombobox'
import { useAuthContext } from '../../contexts/AuthContext'
import { dateOnlyToTimestamptz, parseDateOnlyYmd } from '../../utils/dateOnly'
import {
  formatTicketAttachmentSize,
  isAllowedTicketAttachmentFile,
  isTicketAttachmentImage,
  isTicketAttachmentPdf,
  isTicketAttachmentWithinSizeLimit,
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_ATTACHMENT_MAX_MB,
} from './qualidadeAttachments'

const selectClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'

const SEVERITY_OPTIONS: { value: Severity; label: string; dotClass: string; description: string }[] = [
  {
    value: 'low',
    label: 'Baixa',
    dotClass: 'bg-primary-500',
    description:
      'Não impacta a operação ou o atendimento ao cliente/corretor. Pode ser tratado posteriormente, sem urgência.',
  },
  {
    value: 'medium',
    label: 'Média',
    dotClass: 'bg-yellow-400',
    description:
      'Impacto moderado na operação ou no atendimento. Não impede a continuidade, mas pode gerar retrabalho, lentidão ou insatisfação se não tratado em tempo adequado.',
  },
  {
    value: 'high',
    label: 'Alta',
    dotClass: 'bg-red-500',
    description:
      'Impacto crítico na operação ou no atendimento. Pode causar parada da operação, indisponibilidade de serviço ou prejuízo relevante para corretores e clientes. Requer atuação imediata.',
  },
]

const PRIORITY_HELP_CONTENT = (
  <div className="space-y-3 text-left">
    {SEVERITY_OPTIONS.map(({ label, dotClass, description }) => (
      <div key={label}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`h-2 w-2 rounded-full shrink-0 ${dotClass}`} aria-hidden="true" />
          <span className="font-semibold text-white">{label}</span>
        </div>
        <p className="text-gray-200 leading-relaxed">{description}</p>
      </div>
    ))}
  </div>
)

const requiredAsterisk = (
  <span className="text-red-600 dark:text-red-400" aria-hidden="true">
    *
  </span>
)

/** Valida par opcional unidade/valor do prazo máximo corrido. */
function isMaxDeadlinePairValid(editing: Partial<QualityTicket>): boolean {
  const unit = editing.max_deadline_unit
  const value = editing.max_deadline_value
  const hasUnit = unit != null
  const hasValue = value != null && value > 0
  if (!hasUnit && !hasValue) return true
  return hasUnit && hasValue
}

/** Validação de campos obrigatórios na UI (novo ticket / edição). */
export function isTicketFormValid(
  editing: Partial<QualityTicket>,
  options?: { severityRequired?: boolean; maxDeadlineEditable?: boolean },
): boolean {
  const severityRequired = options?.severityRequired ?? true
  const maxDeadlineEditable = options?.maxDeadlineEditable ?? true
  if (!(editing.title || '').trim()) return false
  if (!(editing.description || '').trim()) return false
  const kind = editing.kind ?? 'incident'
  if (kind === 'incident' && !(editing.justification || '').trim()) return false
  if (severityRequired && !editing.severity) return false
  if (!editing.requester_user_id) return false
  const occurredDate = parseDateOnlyYmd(editing.occurred_at) ?? ''
  if (!occurredDate) return false
  if (!editing.category_id) return false
  if (!editing.department_id) return false
  if (editing.kind === 'not_applicable' && !editing.improcedent_reason_id) return false
  if (maxDeadlineEditable && !isMaxDeadlinePairValid(editing)) return false
  return true
}

function patchKindChange(prev: Partial<QualityTicket> | null, kind: TicketKind): Partial<QualityTicket> {
  const base = { ...(prev || {}), kind }
  if (kind === 'not_applicable') {
    return { ...base, ticket_type_id: null, origin_id: null }
  }
  if (kind === 'improvement' || kind === 'suggestion') {
    return {
      ...base,
      improcedent_reason_id: null,
      ticket_type_id: null,
      policy_number: null,
      convenio_code: null,
    }
  }
  return { ...base, improcedent_reason_id: null }
}

export interface TicketEditModalProps {
  open: boolean
  editing: Partial<QualityTicket>
  onEditingChange: React.Dispatch<React.SetStateAction<Partial<QualityTicket> | null>>
  stages: QualityTicketStage[]
  categories: QualityCategory[]
  departments: { id: string; name: string; display_name: string }[]
  activeProfiles: QualityActiveProfile[]
  incidentTypes: QualityTicketType[]
  incidentOrigins: QualityTicketOrigin[]
  /** Mapa typeId → originId[] para filtrar tipos por origem selecionada. */
  typeOriginLinks?: Map<string, string[]>
  improcedentReasons?: QualityImprocedentReason[]
  /** Parâmetros de qualidade (departamento_qualidade, SLA, etc.). */
  qualityParameters?: QualityParameter[]
  canUseNotApplicableKind?: boolean
  saving: boolean
  onClose: () => void
  onSave: (pendingFiles: File[], toDelete: QualityTicketAttachment[]) => void | Promise<void>
}

/** @deprecated Use TicketEditModalProps */
export type IncidentEditModalProps = TicketEditModalProps

export const TicketEditModal: React.FC<TicketEditModalProps> = ({
  open,
  editing,
  onEditingChange,
  stages,
  categories,
  departments,
  activeProfiles,
  incidentTypes,
  incidentOrigins,
  typeOriginLinks,
  improcedentReasons = [],
  qualityParameters = [],
  canUseNotApplicableKind = false,
  saving,
  onClose,
  onSave,
}) => {
  const { profile } = useAuthContext()
  const userDepartmentId = (profile as { department_id?: string | null } | null)?.department_id ?? null
  const isQualityDeptUser = useMemo(
    () => isQualityDepartmentUser(userDepartmentId, qualityParameters),
    [userDepartmentId, qualityParameters],
  )
  const effectiveQualityDeptAccess = qualityParameters.length > 0 ? isQualityDeptUser : canUseNotApplicableKind

  const effectiveKind = editing.kind ?? 'incident'
  const hideIncidentOnlyFields = effectiveKind === 'improvement' || effectiveKind === 'suggestion'
  const justificationRequired = effectiveKind === 'incident'
  const descriptionHint = descriptionHintForKind(effectiveKind)
  const justificationHint = justificationHintForKind(effectiveKind)
  const isNotApplicable = effectiveKind === 'not_applicable'
  const showKindReadOnly = isNotApplicable && !effectiveQualityDeptAccess
  const kindsForForm = ticketKindsForForm(effectiveQualityDeptAccess, editing.kind)
  const showQualityOnlyFields = effectiveQualityDeptAccess
  const showPriorityField = showQualityOnlyFields
  const severityRequired = showQualityOnlyFields
  const editingStage = useMemo(
    () => (editing.stage_id ? stages.find(s => s.id === editing.stage_id) : undefined),
    [editing.stage_id, stages]
  )

  // Tipos filtrados pela origem selecionada. Se não há mapa de vínculos, exibe todos (compatibilidade).
  const typesForOrigin = useMemo(() => {
    if (!typeOriginLinks || typeOriginLinks.size === 0) return incidentTypes
    if (!editing.origin_id) return []
    const allowed = incidentTypes.filter(t => typeOriginLinks.get(t.id)?.includes(editing.origin_id!))
    // Se o ticket já tem um tipo salvo que não consta no filtro (dado legado), inclui para não perder referência
    if (editing.ticket_type_id && !allowed.find(t => t.id === editing.ticket_type_id)) {
      const legacy = incidentTypes.find(t => t.id === editing.ticket_type_id)
      if (legacy) return [...allowed, legacy]
    }
    return allowed
  }, [editing.origin_id, editing.ticket_type_id, incidentTypes, typeOriginLinks])

  // Anexos já salvos no banco
  const [savedAttachments, setSavedAttachments] = useState<QualityTicketAttachment[]>([])
  // Arquivos novos a enviar após salvar
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  // Previews de objetos URL para imagens pendentes (null = arquivo não-imagem)
  const [pendingPreviews, setPendingPreviews] = useState<(string | null)[]>([])
  // Anexos marcados para remoção
  const [toDelete, setToDelete] = useState<QualityTicketAttachment[]>([])
  // URLs assinadas dos anexos salvos
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({})

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  // Carregar anexos ao abrir em modo edição
  useEffect(() => {
    if (!open) {
      setPendingFiles([])
      setPendingPreviews([])
      setToDelete([])
      setSavedAttachments([])
      setSignedUrls({})
      setAttachmentError(null)
      return
    }
    if (editing.id) {
      qualityService.listTicketAttachments(editing.id).then(async attachments => {
        setSavedAttachments(attachments)
        const urls: Record<string, string> = {}
        await Promise.all(
          attachments.map(async att => {
            try {
              urls[att.id] = await qualityService.getAttachmentSignedUrl(att.file_path)
            } catch { /* ignora */ }
          })
        )
        setSignedUrls(urls)
      })
    }
  }, [open, editing.id])

  // Revogar object URLs ao desmontar
  useEffect(() => {
    return () => { pendingPreviews.forEach(url => { if (url) URL.revokeObjectURL(url) }) }
  }, [pendingPreviews])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const valid: File[] = []
    const invalid: string[] = []
    const tooLarge: string[] = []
    for (const file of files) {
      if (!isAllowedTicketAttachmentFile(file)) {
        invalid.push(file.name)
        continue
      }
      if (!isTicketAttachmentWithinSizeLimit(file)) {
        tooLarge.push(file.name)
        continue
      }
      valid.push(file)
    }

    const errors: string[] = []
    if (invalid.length) {
      errors.push(`Formato não permitido: ${invalid.join(', ')}. Use imagens, PDF ou e-mails (.msg, .eml).`)
    }
    if (tooLarge.length) {
      errors.push(`Arquivo(s) acima de ${TICKET_ATTACHMENT_MAX_MB} MB: ${tooLarge.join(', ')}.`)
    }
    if (errors.length) {
      setAttachmentError(errors.join(' '))
    } else {
      setAttachmentError(null)
    }

    if (!valid.length) {
      e.target.value = ''
      return
    }

    const newPreviews = valid.map(f =>
      f.type.startsWith('image/') || isTicketAttachmentImage({ file_name: f.name, mime_type: f.type })
        ? URL.createObjectURL(f)
        : null
    )
    setPendingFiles(prev => [...prev, ...valid])
    setPendingPreviews(prev => [...prev, ...newPreviews])
    e.target.value = ''
  }, [])

  const removePending = (index: number) => {
    const preview = pendingPreviews[index]
    if (preview) URL.revokeObjectURL(preview)
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
    setPendingPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const markToDelete = (att: QualityTicketAttachment) => {
    setToDelete(prev => [...prev, att])
    setSavedAttachments(prev => prev.filter(a => a.id !== att.id))
  }

  const formValid = useMemo(
    () => isTicketFormValid(editing, {
      severityRequired,
      maxDeadlineEditable: showQualityOnlyFields,
    }),
    [editing, severityRequired, showQualityOnlyFields],
  )
  const departmentLabelMap = useMemo(() => buildDepartmentLabelMap(departments), [departments])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between mb-4">
          <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
            {editing.id ? 'Editar ticket' : 'Novo Registro'}
          </Text>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </div>

        <div className="space-y-4">
          {editing.id && editing.ticket_number != null && (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
              <Text size="xs" className="text-gray-500 dark:text-gray-400">ID do ticket</Text>
              <Text size="sm" className="font-mono text-gray-900 dark:text-white font-medium mt-0.5">
                {formatTicketNumber(editing.ticket_number)}
              </Text>
            </div>
          )}

          {/* Tipo de Registro e Prioridade lado a lado como radio buttons */}
          <div className={`grid grid-cols-1 gap-4${showPriorityField ? ' sm:grid-cols-2' : ''}`}>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Registro {requiredAsterisk}
              </Text>
              {showKindReadOnly ? (
                <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300">
                  {TICKET_KIND_LABELS.not_applicable}
                </span>
              ) : (
                <div className="flex flex-wrap gap-4 sm:gap-6">
                  {kindsForForm.map(k => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ticket-kind"
                        value={k}
                        checked={effectiveKind === k}
                        onChange={() => onEditingChange(prev => patchKindChange(prev, k))}
                        className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                        {TICKET_KIND_LABELS[k]}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {showPriorityField && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300">
                    Prioridade {requiredAsterisk}
                  </Text>
                  <Tooltip
                    content={PRIORITY_HELP_CONTENT}
                    position="bottom"
                    constrainWidth
                    maxWidth={400}
                    delay={150}
                    className="inline-flex shrink-0"
                  >
                    <button
                      type="button"
                      className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                      aria-label="Ajuda sobre prioridades"
                    >
                      <QuestionMarkCircleIcon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
                <div className="flex flex-wrap gap-4">
                  {SEVERITY_OPTIONS.map(({ value, label, dotClass }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ticket-severity"
                        value={value}
                        checked={editing.severity === value}
                        onChange={() => onEditingChange(prev => ({ ...(prev || {}), severity: value }))}
                        className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotClass}`} aria-hidden="true" />
                      <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <hr className="border-0 border-t border-gray-200 dark:border-gray-700" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                Solicitante {requiredAsterisk}
              </Text>
              <select
                value={editing.requester_user_id || ''}
                onChange={e => onEditingChange(prev => ({ ...(prev || {}), requester_user_id: e.target.value || null }))}
                className={selectClass}
                required
              >
                <option value="">Selecionar...</option>
                {activeProfiles.map(p => (
                  <option key={p.id} value={p.id}>{formatProfilePickerLabel(p, departmentLabelMap)}</option>
                ))}
              </select>
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                Data de ocorrência {requiredAsterisk}
              </Text>
              <input
                type="date"
                required
                value={parseDateOnlyYmd(editing.occurred_at) ?? ''}
                onChange={e =>
                  onEditingChange(prev => ({
                    ...(prev || {}),
                    occurred_at: e.target.value ? dateOnlyToTimestamptz(e.target.value) : null,
                  }))
                }
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
              Título {requiredAsterisk}
            </Text>
            <Input
              required
              placeholder="Descreva o ticket"
              value={editing.title || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onEditingChange(prev => ({ ...(prev || {}), title: e.target.value }))
              }
            />
          </div>

          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
              {descriptionLabelForKind(effectiveKind)} {requiredAsterisk}
              {descriptionHint && (
                <>
                  {' '}
                  <span className="italic font-normal text-gray-500 dark:text-gray-400">
                    {descriptionHint}
                  </span>
                </>
              )}
            </Text>
            <textarea
              rows={3}
              required
              placeholder={descriptionPlaceholderForKind(effectiveKind)}
              value={editing.description || ''}
              onChange={e => onEditingChange(prev => ({ ...(prev || {}), description: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
              {justificationLabelForKind(effectiveKind)}
              {justificationRequired && <> {requiredAsterisk}</>}
              {justificationHint && (
                <>
                  {' '}
                  <span className="italic font-normal text-gray-500 dark:text-gray-400">
                    {justificationHint}
                  </span>
                </>
              )}
            </Text>
            <textarea
              rows={3}
              required={justificationRequired}
              placeholder="Explique o contexto ou a razão do registro..."
              value={editing.justification || ''}
              onChange={e => onEditingChange(prev => ({ ...(prev || {}), justification: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <hr className="border-0 border-t border-gray-200 dark:border-gray-700" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                Categoria {requiredAsterisk}
              </Text>
              <select
                value={editing.category_id || ''}
                onChange={e => onEditingChange(prev => ({ ...(prev || {}), category_id: e.target.value || null }))}
                className={selectClass}
                required
              >
                <option value="">Selecionar...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.display_name}</option>
                ))}
              </select>
            </div>

            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                {DEPARTMENT_RESP_LABEL} {requiredAsterisk}
              </Text>
              <select
                value={editing.department_id || ''}
                onChange={e => onEditingChange(prev => ({ ...(prev || {}), department_id: e.target.value || null }))}
                className={selectClass}
                required
              >
                <option value="">Selecionar...</option>
                {departments.map(dep => (
                  <option key={dep.id} value={dep.id}>{dep.display_name}</option>
                ))}
              </select>
            </div>
          </div>

          {editing.id && (
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                Etapa {requiredAsterisk}
              </Text>
              <select
                value={editing.stage_id || ''}
                onChange={e => onEditingChange(prev => ({ ...(prev || {}), stage_id: e.target.value }))}
                className={selectClass}
              >
                <option value="">Selecionar...</option>
                {stages.map(st => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            </div>
          )}

          {isNotApplicable ? (
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                Registo improcedente {requiredAsterisk}
              </Text>
              <select
                value={editing.improcedent_reason_id || ''}
                onChange={e =>
                  onEditingChange(prev => ({
                    ...(prev || {}),
                    improcedent_reason_id: e.target.value || null,
                  }))
                }
                className={selectClass}
                required
              >
                <option value="">Selecionar...</option>
                {improcedentReasons.map(r => (
                  <option key={r.id} value={r.id}>{r.display_name}</option>
                ))}
                {editing.improcedent_reason_id &&
                  !improcedentReasons.some(r => r.id === editing.improcedent_reason_id) &&
                  editing.improcedent_reason?.display_name && (
                    <option value={editing.improcedent_reason_id}>{editing.improcedent_reason.display_name}</option>
                  )}
              </select>
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-4${hideIncidentOnlyFields ? '' : ' sm:grid-cols-2'}`}>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Onde</Text>
                <select
                  value={editing.origin_id || ''}
                  onChange={e => {
                    const newOriginId = e.target.value || null
                    onEditingChange(prev => {
                      const currentTypeId = prev?.ticket_type_id ?? null
                      const typeStillValid = newOriginId && currentTypeId
                        ? (typeOriginLinks?.get(currentTypeId)?.includes(newOriginId) ?? true)
                        : false
                      return {
                        ...(prev || {}),
                        origin_id: newOriginId,
                        ticket_type_id: typeStillValid ? currentTypeId : null,
                      }
                    })
                  }}
                  className={selectClass}
                >
                  <option value="">- Não informado -</option>
                  {incidentOrigins.map(o => (
                    <option key={o.id} value={o.id}>{o.display_name}</option>
                  ))}
                </select>
              </div>

              {!hideIncidentOnlyFields && (
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Motivo</Text>
                  <select
                    value={editing.ticket_type_id || ''}
                    onChange={e => onEditingChange(prev => ({ ...(prev || {}), ticket_type_id: e.target.value || null }))}
                    disabled={!!typeOriginLinks && typeOriginLinks.size > 0 && !editing.origin_id}
                    className={selectClass + (!editing.origin_id && typeOriginLinks && typeOriginLinks.size > 0 ? ' opacity-50 cursor-not-allowed' : '')}
                  >
                    <option value="">
                      {!!typeOriginLinks && typeOriginLinks.size > 0 && !editing.origin_id
                        ? '- Selecione onde primeiro -'
                        : '- Não informado -'}
                    </option>
                    {typesForOrigin.map(t => (
                      <option key={t.id} value={t.id}>{t.display_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {editingStage?.is_closed && (
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Resolução</Text>
              <textarea
                rows={3}
                placeholder="Descreva como o ticket foi resolvido..."
                value={editing.resolution || ''}
                onChange={e => onEditingChange(prev => ({ ...(prev || {}), resolution: e.target.value || null }))}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          )}

          <hr className="border-0 border-t border-gray-200 dark:border-gray-700" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Link externo</Text>
              <Input
                type="url"
                placeholder="https://..."
                value={editing.external_link || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onEditingChange(prev => ({ ...(prev || {}), external_link: e.target.value || null }))
                }
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Corretor</Text>
              <BrokerCombobox
                value={editing.broker_id ?? null}
                initialBroker={editing.broker ?? null}
                onChange={(id, broker) =>
                  onEditingChange(prev => ({
                    ...(prev || {}),
                    broker_id: id,
                    broker: broker ?? undefined,
                  }))
                }
              />
            </div>
          </div>

          {!hideIncidentOnlyFields && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Apólice</Text>
                <PolicyNumberCombobox
                  value={editing.policy_number ?? null}
                  onChange={val =>
                    onEditingChange(prev => ({
                      ...(prev || {}),
                      policy_number: val,
                    }))
                  }
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Subgrupo</Text>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Somente números"
                  value={formatOptionalDigitsField(editing.convenio_code)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onEditingChange(prev => ({
                      ...(prev || {}),
                      convenio_code: parseOptionalDigitsField(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
          )}

          {/* Seção de anexos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">Anexos</Text>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
              >
                <PaperClipIcon className="h-4 w-4" />
                Adicionar arquivo
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={TICKET_ATTACHMENT_ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-2">
              Imagens (JPG, PNG, GIF, WebP), PDF ou e-mails do Outlook (.msg, .eml). Máximo {TICKET_ATTACHMENT_MAX_MB} MB por arquivo.
            </Text>
            {attachmentError && (
              <Text size="xs" className="text-red-600 dark:text-red-400 mb-2">
                {attachmentError}
              </Text>
            )}

            {(savedAttachments.length > 0 || pendingFiles.length > 0) ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {/* Anexos já salvos */}
                {savedAttachments.map(att => (
                  <div key={att.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                    {isTicketAttachmentImage(att) && signedUrls[att.id] ? (
                      <img
                        src={signedUrls[att.id]}
                        alt={att.file_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-center">
                        {isTicketAttachmentPdf(att) ? (
                          <DocumentTextIcon className="h-7 w-7 text-primary-500 dark:text-primary-400 shrink-0" />
                        ) : (
                          <EnvelopeIcon className="h-7 w-7 text-primary-500 dark:text-primary-400 shrink-0" />
                        )}
                        <p className="text-[10px] text-gray-600 dark:text-gray-300 line-clamp-2 break-all">{att.file_name}</p>
                        {att.file_size != null && (
                          <p className="text-[9px] text-gray-400">{formatTicketAttachmentSize(att.file_size)}</p>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => markToDelete(att)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remover anexo"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                    {isTicketAttachmentImage(att) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-[10px] truncate">{att.file_name}</p>
                      </div>
                    )}
                  </div>
                ))}

                {/* Anexos pendentes */}
                {pendingFiles.map((file, idx) => {
                  const preview = pendingPreviews[idx]
                  const isImage = preview != null
                  const isPdf = isTicketAttachmentPdf({ file_name: file.name, mime_type: file.type })
                  return (
                  <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-dashed border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/20">
                    {isImage ? (
                      <img
                        src={preview}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-center">
                        {isPdf ? (
                          <DocumentTextIcon className="h-7 w-7 text-primary-600 dark:text-primary-400 shrink-0" />
                        ) : (
                          <EnvelopeIcon className="h-7 w-7 text-primary-600 dark:text-primary-400 shrink-0" />
                        )}
                        <p className="text-[10px] text-gray-700 dark:text-gray-200 line-clamp-2 break-all">{file.name}</p>
                        <p className="text-[9px] text-gray-500">{formatTicketAttachmentSize(file.size)}</p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePending(idx)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Cancelar"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                    {isImage && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-[10px] truncate">{file.name}</p>
                      </div>
                    )}
                    <span className="absolute top-1 left-1 bg-primary-500 text-white text-[9px] font-bold px-1 rounded">NOVO</span>
                  </div>
                  )
                })}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col items-center gap-1 text-gray-400 dark:text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-colors"
              >
                <PaperClipIcon className="h-6 w-6" />
                <span className="text-xs">Clique para adicionar imagens, PDF ou e-mails (.msg, .eml)</span>
              </button>
            )}
          </div>

          {showQualityOnlyFields && (
            <>
          <hr className="border-0 border-t border-gray-200 dark:border-gray-700 mt-6" />

          {/* Prazo máximo corrido */}
          <div className="mt-6">
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-3 font-medium">
              Prazo máximo corrido
            </Text>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Unidade</Text>
                <select
                  value={editing.max_deadline_unit || ''}
                  onChange={e => {
                    const unit = (e.target.value || null) as MaxDeadlineUnit | null
                    onEditingChange(prev => ({
                      ...(prev || {}),
                      max_deadline_unit: unit,
                      max_deadline_value: unit ? prev?.max_deadline_value ?? null : null,
                    }))
                  }}
                  className={selectClass}
                >
                  <option value="">—</option>
                  {(Object.entries(MAX_DEADLINE_UNIT_LABELS) as [MaxDeadlineUnit, string][]).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Valor</Text>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="Ex.: 5"
                  value={editing.max_deadline_value ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value.trim()
                    const parsed = raw === '' ? null : parseInt(raw, 10)
                    const value = parsed != null && !isNaN(parsed) && parsed > 0 ? parsed : null
                    onEditingChange(prev => ({
                      ...(prev || {}),
                      max_deadline_value: value,
                      max_deadline_unit: value != null ? (prev?.max_deadline_unit ?? 'days') : null,
                    }))
                  }}
                />
              </div>
            </div>
            <div className="mt-4">
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Obs. do prazo</Text>
              <textarea
                rows={2}
                placeholder="Observações sobre o prazo máximo (opcional)"
                value={editing.max_deadline_notes ?? ''}
                onChange={e =>
                  onEditingChange(prev => ({
                    ...(prev || {}),
                    max_deadline_notes: e.target.value === '' ? null : e.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[60px]"
              />
            </div>
          </div>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            variant="solid"
            onClick={() => void onSave(pendingFiles, toDelete)}
            disabled={saving || !formValid}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** @deprecated Use TicketEditModal */
export const IncidentEditModal = TicketEditModal
